// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Hardcoded school ID for MVP.
 * Multi-school support will be added post-MVP by making this dynamic.
 */
export const SCHOOL_ID = "school_demo";

// =============================================================================
// USER TYPES
// =============================================================================

export type UserRole = "admin" | "parent" | "student";

export interface User {
  id: string;
  email: string;
  /** Display name (e.g. "Alejandro") */
  name?: string;
  role: UserRole;
  schoolId: string;
  /** Set when role is "student": the student document ID this user represents (for enrollments) */
  childId?: string;
  createdAt: Date;
}

/**
 * Custom claims stored in Firebase Auth token.
 * Set by Cloud Function on user creation.
 */
export interface CustomClaims {
  role: UserRole;
  schoolId: string;
}

// =============================================================================
// DOMAIN TYPES
// =============================================================================

export interface Child {
  id: string;
  name: string;
  parentId?: string;
  schoolId: string;
  createdAt: Date;
}

export interface Class {
  id: string;
  name: string;
  monthlyPrice: number;
  isActive: boolean;
  schoolId: string;
  createdAt: Date;
}

export interface Enrollment {
  id: string;
  childId: string;
  classId: string;
  month: string; // Format: "YYYY-MM"
  schoolId: string;
  /** Set when enrollment was created by student (their auth uid) */
  studentId?: string;
  createdAt: Date;
}

export type PaymentStatus = "pending" | "paid" | "failed";

export interface Payment {
  id: string;
  parentId: string;
  month: string; // Format: "YYYY-MM"
  amount: number;
  status: PaymentStatus;
  enrollmentIds: string[];
  schoolId: string;
  createdAt: Date;
  paidAt?: Date;
}

// =============================================================================
// API TYPES (Cloud Function requests/responses)
// =============================================================================

export interface CreateEnrollmentRequest {
  childId: string;
  classId: string;
  month: string;
}

export interface CreateEnrollmentResponse {
  enrollmentId: string;
}

export interface CreateEnrollmentAsStudentRequest {
  classId: string;
  month: string; // YYYY-MM
}

export interface CreateEnrollmentAsStudentResponse {
  enrollmentId: string;
}

export interface CreateClassRequest {
  name: string;
  monthlyPrice: number;
  isActive?: boolean;
  scheduleDay?: string;
  scheduleTime?: string;
  description?: string;
  /** Optional capacity (slots). Omit or 0 = no limit. */
  cupos?: number;
}

export interface CreateClassResponse {
  classId: string;
}

export interface UpdateClassRequest {
  classId: string;
  name?: string;
  monthlyPrice?: number;
  isActive?: boolean;
  scheduleDay?: string;
  scheduleTime?: string;
  description?: string;
  cupos?: number;
}

export interface UpdateClassResponse {
  success: boolean;
}

export interface DeleteClassRequest {
  classId: string;
}

export interface DeleteClassResponse {
  success: boolean;
}

export interface DeleteEnrollmentRequest {
  /** Student (child) document ID - enrollments live under students/{studentId}/enrollments */
  studentId: string;
  enrollmentId: string;
}

// =============================================================================
// VIEW TYPES (Frontend-specific)
// =============================================================================

export interface ChildWithClasses extends Child {
  enrollments: Array<{
    enrollment: Enrollment;
    class: Class;
  }>;
}

export interface MonthlyBreakdown {
  children: ChildWithClasses[];
  totalAmount: number;
  payment: Payment | null;
}

// Admin: Assign Student to Parent
export interface StudentForAdmin {
  id: string;
  name: string;
  parentId?: string;
  /** Creation time in ms (for month filter). */
  createdAtMs?: number;
}

export interface ParentForAdmin {
  id: string;
  email: string;
}

export interface ListStudentsForAdminResponse {
  students: StudentForAdmin[];
}

export interface ListParentsForAdminResponse {
  parents: ParentForAdmin[];
}

export interface AssignStudentToParentRequest {
  childId: string;
  parentId: string;
}

// Admin: Classes with enrollments
export interface ClassEnrollmentRow {
  enrollmentId: string;
  childId: string;
  childName: string;
  parentId: string;
  parentEmail: string | null;
  month: string;
}

export interface GetClassesWithEnrollmentsResponse {
  classes: Array<{
    id: string;
    name: string;
    monthlyPrice: number;
    isActive: boolean;
    scheduleDay?: string;
    scheduleTime?: string;
    description?: string;
    cupos?: number;
    maxCapacity?: number;
    currentEnrollment?: number;
  }>;
  enrollmentsByClassId: Record<string, ClassEnrollmentRow[]>;
}

// Forum: classes from Firebase (getClassesForForum)
export interface ForumClassFromApi {
  id: string;
  name: string;
  monthlyPrice: number;
  /** Capacity (slots). Omit or 0 = no limit. */
  cupos?: number;
  maxCapacity?: number;
  currentEnrollment?: number;
  nameEn?: string;
  nameEs?: string;
  /** From admin: day(s), e.g. "Lunes, Martes" */
  scheduleDay?: string;
  /** English override for scheduleDay; falls back to scheduleDay if absent */
  scheduleDayEn?: string;
  /** From admin: time range, e.g. "10:00 – 11:30" */
  scheduleTime?: string;
  scheduleEn?: string;
  scheduleEs?: string;
  time?: string;
  externalLink?: string;
  externalEmail?: string;
}

export interface GetClassesForForumResponse {
  classes: ForumClassFromApi[];
}

// Public registration (Talent School form - no auth)
// Fiscal and registration extension (Phase 1) — DGII NCF codes for Dominican Republic
export type DocumentType = "RNC" | "CEDULA";
/** DGII: B01 Crédito Fiscal, B02 Consumidor Final */
export type ComprobanteType = "B01" | "B02";
export type SchoolType = "AMERICAN_SCHOOL" | "EXTERNO";
export type PaymentFrequency = "BIMESTRAL" | "CUATRIMESTRAL";

export interface FiscalData {
  fiscalName: string;
  documentType: DocumentType;
  documentNumber: string;
  /** Kept for backward compat with old records; no longer collected from users */
  fiscalAddress?: string;
  comprobanteType: ComprobanteType;
  requireNCF?: boolean;
  ncfNumber?: string;
}

export interface SubmitRegistrationRequest {
  child: { childName: string; school: string; age: string };
  parents: {
    housePhone: string;
    motherName: string;
    motherCell: string;
    fatherName: string;
    fatherCell: string;
    parentEmail: string;
  };
  emergencyContacts: Array<{ name: string; relation: string; phone: string }>;
  medicalConditions: string;
  selectedClasses: string[];
  totalPrice: number;
  paymentMethod: "ONLINE" | "LOCAL";
  /** Required only when user wants comprobante fiscal; omit when "No deseo comprobante" */
  fiscalData?: FiscalData;
  schoolType: SchoolType;
  /** Required when schoolType is AMERICAN_SCHOOL; defaults to registrationId when EXTERNO */
  studentCode?: string;
  paymentFrequency: PaymentFrequency;
  /** SAP card code for the selected student, if chosen from the dropdown */
  cardCode?: string;
}

/** Response from getRegistrationForRestore (same shape as registration doc for form pre-fill). */
export interface GetRegistrationForRestoreResponse {
  registrationId: string;
  child: { childName: string; school: string; age: string };
  parents: {
    housePhone: string;
    motherName: string;
    motherCell: string;
    fatherName: string;
    fatherCell: string;
    parentEmail: string;
  };
  emergencyContacts: Array<{ name: string; relation: string; phone: string }>;
  medicalConditions: string;
  selectedClasses: string[];
  totalPrice: number;
  paymentMethod: "ONLINE" | "LOCAL";
  fiscalData?: FiscalData;
  schoolType?: SchoolType;
  studentCode?: string;
  paymentFrequency?: PaymentFrequency;
  studentId?: string | null;
}

/** Azul Hosted Page: create session response */
export interface CreateAzulPaymentSessionResponse {
  actionUrl: string;
  formFields: Record<string, string>;
}

export interface SubmitRegistrationResponse {
  /** Use this ID to view pending payment and pay. Student is created when payment is completed. */
  registrationId: string;
}

/** Response from getCurrentClassesByStudentCode (renewal: pre-fill class selection) */
export interface GetCurrentClassesByStudentCodeResponse {
  classIds: string[];
}

export interface CheckStudentCodeAvailableResponse {
  available: boolean;
}

// Public access by registration ID (no auth)
export interface ClassInfo {
  id: string;
  name: string;
}

export interface GetPendingByRegistrationIdResponse {
  /** Registration document ID (use for payment links). Returned when lookup is by matrícula. */
  registrationId: string;
  childName: string;
  totalPrice: number;
  paymentMethod: string;
  canPay: boolean;
  /** When true, user can change class selection (pending payment) */
  canEditClasses?: boolean;
  /** Current selected class IDs (for editing selection) */
  selectedClasses?: string[];
  /** Classes selected in the registration (pending until payment) */
  pendingClasses: ClassInfo[];
  /** Classes the student is currently enrolled in (after payment) */
  currentClasses: ClassInfo[];
  message?: string;
  /** When paying a specific month (monthly flow) */
  pendingMonthId?: string;
  pendingMonth?: string;
  /** Set when user requested comprobante fiscal at registration */
  fiscalData?: FiscalData;
  /** When true, parents can modify class selection from the Pagos screen (controlled by admin). */
  classSelectionOpen?: boolean;
  /** Payment frequency so parent knows when next payment is due. */
  paymentFrequency?: "BIMESTRAL" | "CUATRIMESTRAL";
  /** Student code (matrícula). Use for /pagos URL and "back" from payment. */
  studentCode?: string;
}

export interface ClassSelectionWindowResponse {
  open: boolean;
}

export interface GetActiveRegistrationMonthResponse {
  month: string; // YYYY-MM
}

export interface SetActiveRegistrationMonthResponse {
  month: string;
}

export interface UpdateRegistrationFiscalDataRequest {
  registrationId: string;
  fiscalData: FiscalData;
}

export interface UpdateRegistrationFiscalDataResponse {
  success: boolean;
}

export interface UpdateRegistrationClassesRequest {
  registrationId: string;
  selectedClasses: string[];
}

export interface UpdateRegistrationClassesResponse {
  registrationId: string;
  totalPrice: number;
}

export interface UpdateRegistrationPaymentMethodRequest {
  registrationId: string;
}

export interface UpdateRegistrationPaymentMethodResponse {
  success: boolean;
}

export interface StudentEnrollmentRow {
  enrollmentId: string;
  classId: string;
  className: string;
}

export interface GetStudentWithEnrollmentsResponse {
  studentId: string;
  childName: string;
  enrollments: StudentEnrollmentRow[];
}

// Admin: list registrations (public form submissions)
export interface RegistrationRecord {
  id: string;
  schoolId: string;
  child: { childName: string; school: string; age: string };
  parents: {
    housePhone: string;
    motherName: string;
    motherCell: string;
    fatherName: string;
    fatherCell: string;
    parentEmail: string;
  };
  emergencyContacts: Array<{ name: string; relation: string; phone: string }>;
  medicalConditions: string;
  selectedClasses: string[];
  totalPrice: number;
  paymentMethod: "ONLINE" | "LOCAL";
  status: string;
  source: string;
  /** Serialized from Firestore (seconds since epoch) or { toMillis } */
  createdAt: { seconds?: number; _seconds?: number; toMillis?: () => number } | null;
  /** When paid, date of payment (YYYY-MM-DD) from payments collection */
  paymentDate?: string;
  fiscalData?: FiscalData;
  schoolType?: SchoolType;
  studentCode?: string;
  paymentFrequency?: PaymentFrequency;
  studentId?: string | null;
  periodStart?: { seconds?: number; toMillis?: () => number } | null;
  periodEnd?: { seconds?: number; toMillis?: () => number } | null;
}

export interface ListRegistrationsRequest {
  status?: "pending" | "paid" | "cancelled" | "awaiting_payment" | "awaiting_manual_payment" | "declined";
  paymentMethod?: "ONLINE" | "LOCAL";
  classId?: string;
}

export interface ListRegistrationsResponse {
  registrations: RegistrationRecord[];
}

export interface ListTransactionsRequest {
  month: string; // YYYY-MM
}

export interface TransactionRecord {
  id: string;
  registrationId: string;
  amount: number;
  studentName: string;
  parentEmail: string;
  paymentMethod: string;
  source: string;
  status: string;
  paidAt?: { seconds: number; nanoseconds: number } | null;
  month: string;
}

export interface ListTransactionsResponse {
  transactions: TransactionRecord[];
}

// =============================================================================
// ADMIN SETTINGS
// =============================================================================

export interface AdminSettings {
  allowOnlinePayment: boolean;
  allowManualPayment: boolean;
  enableFiscalReceipts: boolean;
  defaultComprobanteType: ComprobanteType;
  allowFiscalRequest: boolean;
  registrationOpen: boolean;
  supportEmail: string;
  supportPhone: string;
  schoolAddress: string;
}

export interface GetAdminSettingsResponse {
  settings: AdminSettings;
}

export interface UpdateAdminSettingsResponse {
  settings: AdminSettings;
}

// Admin Finanzas (billingCycles only)
export type BillingCycleStatus = "pending" | "paid" | "overdue" | "cancelled";

export interface BillingCycleRow {
  id: string;
  studentId: string;
  classId: string;
  schoolId: string;
  periodStart: { seconds?: number; _seconds?: number; toMillis?: () => number };
  periodEnd: { seconds?: number; _seconds?: number; toMillis?: () => number };
  dueDate: { seconds?: number; _seconds?: number; toMillis?: () => number };
  amount: number;
  status: BillingCycleStatus;
  paymentFrequency: string;
  classNameSnapshot?: string;
  registrationId?: string;
  paidAt?: { seconds?: number; _seconds?: number } | null;
  createdAt: { seconds?: number; _seconds?: number; toMillis?: () => number };
}

export interface FinanzasTotals {
  totalPaidAmount: number;
  totalPendingAmount: number;
  countPaid: number;
  countPending: number;
}

export interface GetBillingCyclesForAdminRequest {
  statusFilter?: string;
  monthFilter?: string;
}

export interface GetBillingCyclesForAdminResponse {
  cycles: BillingCycleRow[];
  totals: FinanzasTotals;
}

// Finance module: raw API response + computed for UI
export type TsLike = { seconds?: number; _seconds?: number; toMillis?: () => number };

export interface RawBillingCycleDoc {
  id: string;
  studentId: string;
  classId: string;
  schoolId: string;
  /** YYYY-MM from API. Used for month filter and period display. */
  month?: string;
  /** paid | pending from API. Finanzas totals use this. */
  status?: "paid" | "pending";
  /** MANUAL (admin mark as paid) or AZUL (card). For Finanzas display. */
  paymentMethod?: "MANUAL" | "AZUL";
  /** Group cycles from same registration payment. For Finanzas grouped view. */
  paymentGroupId?: string;
  /** DGII comprobante type (B01/B02/B14/B15) when NCF requested. */
  comprobanteType?: string;
  /** Whether NCF was requested. Finanzas shows type vs "Consumidor Final". */
  requireNCF?: boolean;
  periodStart?: TsLike;
  periodEnd?: TsLike;
  dueDate?: TsLike;
  amount: number;
  classNameSnapshot?: string;
  studentName?: string;
  enrollmentId?: string;
  createdAt?: TsLike;
}

export interface RawPaymentDoc {
  id: string;
  billingCycleId: string;
  studentId: string;
  studentName?: string;
  amount: number;
  method: string;
  origin: string;
  schoolId?: string;
  createdAt: TsLike;
}

export type BillingCycleStatusDisplay = "Pendiente de pago" | "Pagado";

/** Single billing cycle row (legacy flat view). */
export interface BillingCycleForUI {
  id: string;
  studentId: string;
  studentName: string;
  className: string;
  period: string;
  periodYYYYMM: string;
  dueDate?: TsLike | null;
  amount: number;
  status: BillingCycleStatusDisplay;
  paymentDate: TsLike | null;
  paymentMethod?: "MANUAL" | "AZUL";
  paymentGroupId?: string;
  comprobanteType?: string;
  requireNCF?: boolean;
}

/** Grouped payment row for Finanzas: one row per payment (registration), aggregated from billing cycles or pending registration. */
export interface PaymentGroupRow {
  id: string;
  studentId: string;
  studentName: string;
  classNames: string[];
  periodYYYYMM: string;
  periodLabel: string;
  totalAmount: number;
  status: BillingCycleStatusDisplay;
  paymentMethod?: "MANUAL" | "AZUL";
  /** When row is from a pending registration: "En línea" | "En escuela". */
  paymentMethodDisplay?: string;
  /** B01/B02/B14/B15 when requireNCF; otherwise "Consumidor Final". */
  comprobanteDisplay: string;
}

/** Admin Finanzas: billing cycles only. Status from billingCycle.status (paid | pending). */
export interface GetBillingCyclesForAdminResponse {
  billingCycles: RawBillingCycleDoc[];
}

/** @deprecated Use GetBillingCyclesForAdminResponse. Kept for compatibility. */
export interface GetBillingCyclesAndPaymentsResponse {
  billingCycles: RawBillingCycleDoc[];
}

// =============================================================================
// PARENT PORTAL — MY REGISTRATIONS
// =============================================================================

export interface MyRegistrationSummary {
  id: string;
  status: string;
  studentCode: string;
  studentId: string | null;
  totalPrice: number;
  paymentMethod: string;
  month: string;
  createdAt: unknown;
  childName: string;
  selectedClasses: string[];
  paymentFrequency: string;
  active: boolean;
}

export interface GetMyRegistrationsResponse {
  registrations: MyRegistrationSummary[];
}

export interface ActivePeriod {
  classId: string;
  classNameSnapshot?: string;
  /** First YYYY-MM when the student may re-register for this class. */
  reEnrollMonth: string;
}

export interface LookupStudentForParentResponse {
  studentId: string;
  studentName: string;
  cardCode: string;
  registrations: MyRegistrationSummary[];
  activePeriods: ActivePeriod[];
}

/** One row for Excel export (filtered students). */
export interface StudentPaymentRecord {
  studentName: string;
  parentName: string;
  parentEmail: string;
  className: string;
  month: string;
  amount: number;
  paymentStatus: string;
  paymentDate: string;
}
